import { CaretUpDownIcon } from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import filter from 'lodash/filter';
import { CircleCheckIcon, CircleXIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { GenericTable } from '@/modules/common';

import {
  EventParticipant,
  EventParticipantType,
  ParticipantsTableFiltersType,
} from '../types';
import { ParticipantNotifications } from './participant-notifications';
import { ParticipantTableActions } from './participant-table-actions';
import { ParticipantTypeSelect } from './participant-type-select';
import { ParticipantUsageLeftInput } from './participant-usage-left-input';
import { ParticipantsTableFilters } from './participants-table-filters';
import { SendSmsDialog } from './send-sms-dialog';

interface ParticipantsTableProps {
  eventId: string;
  editMode?: boolean;
  participants: EventParticipant[];
  onParticipantTypesClick: () => void;
  participantTypes: EventParticipantType[];
}

export function ParticipantsTable({
  eventId,
  participants,
  editMode = false,
  participantTypes,
  onParticipantTypesClick,
}: ParticipantsTableProps) {
  const [isSendSmsDialogOpen, setIsSendSmsDialogOpen] = useState(false);
  const [filters, setFilters] = useState<Partial<ParticipantsTableFiltersType>>(
    {}
  );
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>(
    []
  );

  const filteredParticipants = useMemo(() => {
    if (!Object.keys(filters).length) {
      return participants;
    }

    return filter(participants, (item) => {
      return Object.keys(filters).every((key) => {
        return item[key] === filters[key];
      });
    });
  }, [participants, filters]);

  const columns: ColumnDef<EventParticipant>[] = [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          className="w-5 h-5"
          onCheckedChange={table.toggleAllRowsSelected}
          checked={table.getIsAllRowsSelected()}
        />
      ),
      cell: ({ row }) => (
        <div className="px-1">
          <Checkbox
            className="w-5 h-5"
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onCheckedChange={row.toggleSelected}
          />
        </div>
      ),
    },
    {
      size: 130,
      accessorKey: 'firstName',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            First name
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.firstName}</span>
      ),
    },
    {
      size: 130,
      accessorKey: 'lastName',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Last name
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.lastName}</span>
      ),
    },
    {
      size: 130,
      accessorKey: 'externalId',
      header: 'Has external ID',
      cell: ({ row }) => (
        <span className="line-clamp-1">
          {row.original.externalId ? (
            <CircleCheckIcon color={colors.green[400]} size={28} />
          ) : (
            <CircleXIcon color="hsl(var(--destructive))" size={28} />
          )}
        </span>
      ),
    },
    {
      size: 240,
      accessorKey: 'email',
      header: 'Email Address',
    },
    {
      size: 100,
      accessorKey: 'usagesLeft',
      header: 'Attempts left',
      cell: ({ row }) => (
        <ParticipantUsageLeftInput
          eventId={eventId}
          participantId={row.original.id}
          defaultValue={row.original.usagesLeft.toString()}
        />
      ),
    },
    {
      size: 200,
      accessorKey: 'company',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Company
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.company}</span>
      ),
    },
    {
      size: 200,
      accessorKey: 'jobTitle',
      header: 'Job Title',
    },
    {
      accessorKey: 'status',
      header: 'Status',
    },
    {
      size: 200,
      accessorKey: 'phoneNumber',
      header: 'Phone Number',
    },
    {
      size: 140,
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <ParticipantTypeSelect
          eventId={eventId}
          participantId={row.original.id}
          defaultValue={row.original.type}
          participantTypes={participantTypes}
        />
      ),
    },
    {
      size: 200,
      accessorKey: 'comment',
      header: 'Other',
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="line-clamp-2">{row.original.comment}</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p>{row.original.comment}</p>
          </TooltipContent>
        </Tooltip>
      ),
    },
    {
      size: 260,
      accessorKey: 'notificationNumbers',
      header: 'Alert Recipients',
      cell: ({ row }) => (
        <ParticipantNotifications
          notificationNumbers={row.original.notificationNumbers}
        />
      ),
    },
    {
      size: 40,
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => (
        <ParticipantTableActions
          eventId={eventId}
          participantId={row.original.id}
          externalId={row.original.externalId}
        />
      ),
    },
  ];

  const participantTypesOptions = useMemo(() => {
    return participantTypes.map((type) => ({
      id: type.value,
      value: type.value,
    }));
  }, [participantTypes]);

  const selectedParticipantsIds = useMemo(() => {
    return filteredParticipants
      .filter((participant) => selectedParticipants.includes(participant.id))
      .map((participant) => participant.id);
  }, [selectedParticipants, filters]);

  return (
    <>
      <div className="py-5 flex gap-5 items-center justify-end">
        <div className="text-zinc-400 text-sm text-right">
          <p>Manage existing participant types</p>
          <p>Use the "Participant Types" button to view or update them.</p>
        </div>
        <Button variant="outline" onClick={onParticipantTypesClick}>
          Participant Types
        </Button>
      </div>

      {participants.length > 0 ? (
        <GenericTable
          pinEnabled
          columns={columns}
          name="participants"
          data={filteredParticipants}
          searchable
          searchPlaceholder="Search by name or email"
          searchColumns={['firstName', 'lastName', 'email', 'fullName']}
          emptyCellClassName="h-20"
          sizeStyleProperty="minWidth"
          tableCellClassName="px-2 py-1"
          tableRowClassName="hover:bg-transparent"
          initialState={{
            columnPinning: {
              right: ['actions'],
              left: ['select', 'firstName', 'lastName', 'externalId'],
            },
          }}
          onRowSelectionChange={setSelectedParticipants}
        >
          <div className="flex gap-5 items-center justify-end">
            <ParticipantsTableFilters
              types={participantTypesOptions}
              filters={filters}
              setFilters={setFilters}
            />

            {Object.keys(selectedParticipants).length > 0 && (
              <>
                <Separator orientation="vertical" className="h-8" />
                <Button
                  variant="outline"
                  onClick={() => setIsSendSmsDialogOpen(true)}
                >
                  Send SMS
                </Button>
              </>
            )}
          </div>
        </GenericTable>
      ) : (
        <div className="h-20 text-center text-zinc-500 flex items-center justify-center">
          <p>
            {editMode
              ? 'Here will be uploaded participants. Please upload new one.'
              : 'You can upload participants only when event will be created.'}
          </p>
        </div>
      )}

      {isSendSmsDialogOpen && (
        <SendSmsDialog
          eventId={eventId}
          isOpen={isSendSmsDialogOpen}
          onOpenChange={setIsSendSmsDialogOpen}
          participantIds={selectedParticipantsIds}
        />
      )}
    </>
  );
}
